// ─── Browser / Device ────────────────────────────────────────────────────────

export function parseBrowser(ua: string): string {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\//i.test(ua) || /Opera/i.test(ua)) return 'Opera';
  if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
  if (/Chromium/i.test(ua)) return 'Chromium';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua)) return 'Safari';
  if (/MSIE|Trident/i.test(ua)) return 'IE';
  return 'Unknown';
}

export function parseDevice(ua: string): string {
  if (/iPad/i.test(ua)) return 'Tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'Mobile';
  return 'Desktop';
}

// ─── Session ID ───────────────────────────────────────────────────────────────

let _memorySessionId = '';

function getSessionId(): string {
  const KEY = '_rb_session_id';
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    if (!_memorySessionId) {
      _memorySessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    return _memorySessionId;
  }
}

export const sessionId: string = getSessionId();

// ─── Geo Data ─────────────────────────────────────────────────────────────────

export type GeoData = {
  location: string;     // "London, ON"
  city: string;         // "London"
  region: string;       // "ON"
  country: string;      // "Canada"
  country_code: string; // "CA"
  lat: number | null;
  lng: number | null;
};

const UNKNOWN_GEO: GeoData = {
  location: 'Unknown',
  city: '',
  region: '',
  country: '',
  country_code: '',
  lat: null,
  lng: null,
};

let _geoCache: GeoData | null = null;
let _geoPromise: Promise<GeoData> | null = null;

function buildLocation(city: string, region: string, country: string): string {
  if (city && region)  return `${city}, ${region}`;
  if (city && country) return `${city}, ${country}`;
  return country || 'Unknown';
}

async function fetchGeoJSON(url: string, label: string, ms = 5000): Promise<Record<string, unknown> | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      console.warn(`[Geo] ${label} HTTP ${res.status} — skipping`);
      return null;
    }
    return await res.json() as Record<string, unknown>;
  } catch (e: unknown) {
    clearTimeout(timer);
    const isTimeout = e instanceof Error && e.name === 'AbortError';
    console.warn(`[Geo] ${label} ${isTimeout ? `timed out after ${ms}ms` : 'network/CORS error'}`);
    return null;
  }
}

export async function fetchClientGeo(): Promise<GeoData> {
  if (_geoCache !== null) return _geoCache;
  if (_geoPromise !== null) return _geoPromise;

  _geoPromise = (async (): Promise<GeoData> => {

    // ── Provider 1: geojs.io ──────────────────────────────────────────────────
    // Explicitly sets Access-Control-Allow-Origin: * so no CORS issues.
    // Free, no key, no daily cap. Returns city, country, lat, lon.
    // Docs: https://www.geojs.io/docs/v1/endpoints/geo/
    {
      const j = await fetchGeoJSON('https://get.geojs.io/v1/ip/geo.json', 'geojs.io');
      if (j) {
        const city    = String(j['city']         || '');
        const region  = String(j['region']       || '');   // full region name
        const country = String(j['country']      || '');
        const cc      = String(j['country_code'] || '');
        // geojs returns lat/lon as strings
        const lat     = parseFloat(String(j['latitude']  ?? ''));
        const lng     = parseFloat(String(j['longitude'] ?? ''));
        if (city || country) {
          _geoCache = {
            location: buildLocation(city, cc || region, country),
            city,
            region: cc || region,   // prefer the short code (e.g. "ON") if available
            country,
            country_code: cc,
            lat: isNaN(lat) ? null : lat,
            lng: isNaN(lng) ? null : lng,
          };
          console.log('[Geo] geojs.io success:', _geoCache.location);
          return _geoCache;
        }
        console.warn('[Geo] geojs.io: response ok but city/country empty:', j);
      }
    }

    // ── Provider 2: ipwho.is ──────────────────────────────────────────────────
    // Free, no key, no cap. Sets CORS headers. Returns { success, city,
    // region_code, country, country_code, latitude, longitude }
    {
      const j = await fetchGeoJSON('https://ipwho.is/', 'ipwho.is');
      if (j && j['success'] !== false) {
        const city    = String(j['city']         || '');
        const region  = String(j['region_code']  || '');
        const country = String(j['country']      || '');
        const cc      = String(j['country_code'] || '');
        const lat     = typeof j['latitude']  === 'number' ? j['latitude']  as number : null;
        const lng     = typeof j['longitude'] === 'number' ? j['longitude'] as number : null;
        if (city || country) {
          _geoCache = { location: buildLocation(city, region, country), city, region, country, country_code: cc, lat, lng };
          console.log('[Geo] ipwho.is success:', _geoCache.location);
          return _geoCache;
        }
        console.warn('[Geo] ipwho.is: response ok but city/country empty:', j);
      }
    }

    // ── Provider 3: ipapi.co ──────────────────────────────────────────────────
    // 1,000 req/day free. Returns { error: true, reason } when rate-limited.
    {
      const j = await fetchGeoJSON('https://ipapi.co/json/', 'ipapi.co');
      if (j && !j['error']) {
        const city    = String(j['city']         || '');
        const region  = String(j['region_code']  || '');
        const country = String(j['country_name'] || '');
        const cc      = String(j['country_code'] || '');
        const lat     = typeof j['latitude']  === 'number' ? j['latitude']  as number : null;
        const lng     = typeof j['longitude'] === 'number' ? j['longitude'] as number : null;
        if (city || country) {
          _geoCache = { location: buildLocation(city, region, country), city, region, country, country_code: cc, lat, lng };
          console.log('[Geo] ipapi.co success:', _geoCache.location);
          return _geoCache;
        }
      } else if (j?.['error']) {
        console.warn('[Geo] ipapi.co rate-limited:', j['reason']);
      }
    }

    console.warn('[Geo] All providers failed — location will be Unknown for this session.');
    _geoCache = { ...UNKNOWN_GEO };
    return _geoCache;
  })();

  return _geoPromise;
}

// ─── Back-compat shim ─────────────────────────────────────────────────────────

export async function fetchClientLocation(): Promise<string> {
  return (await fetchClientGeo()).location;
}
